import useUser from '@alltrails/context/hooks/useUser';
import useExperiments from '@alltrails/experiments/hooks/useExperiments';
import type Experiments from '@alltrails/types/Experiments';
import useIsSeoOrViralityTraffic from '@/hooks/useIsSeoOrViralityTraffic';
import { useAppSelector } from '@/utils/redux';

export type ScrollAuthAltTreatment = NonNullable<Experiments['web-growth-rm-scroll-auth-mweb-v2-direct']>['value'];

const useScrollAuthAltTreatment = (isBot: boolean) => {
  const user = useUser();
  // Use mobileBrowser (UA-detected, loaded from server bootstrap into Redux).
  // The viewport value can differ between the server snapshot and the client snapshot
  // (e.g. tablets), causing a hydration re-render that flashes ungated content before the auth wall appears
  const isMobile = useAppSelector(state => !!state.context?.mobileBrowser);
  // The seo vs direct arm is chosen by client-side SEO/virality signals.
  const isSeoOrViralityTraffic = useIsSeoOrViralityTraffic();
  // Eligibility is logged-out mobile users; never bots (keeps crawlers out of the XP, its
  // exposure events, and the DOM-stripping reviews wall).
  const isEligible = !user && isMobile && !isBot;

  const experiments = useExperiments();
  const directValue = experiments['web-growth-rm-scroll-auth-mweb-v2-direct']?.value;
  const seoValue = experiments['web-growth-rm-scroll-auth-mweb-v2-seo']?.value;
  const treatmentValue = isSeoOrViralityTraffic ? seoValue : directValue;

  return {
    isEligible,
    treatmentValue,
    isInTreatment: !!treatmentValue && treatmentValue !== 'control',
    isAppDrawer: treatmentValue === 'app-drawer',
    isMiniInterstitial: treatmentValue === 'mini-interstitial',
    isDismissibleWall: treatmentValue === 'dismissible-wall',
    isReviewsWall: treatmentValue === 'reviews-wall',
    isEscalatingReviewsWall: treatmentValue === 'escalating-reviews-wall'
  };
};

export default useScrollAuthAltTreatment;
